<template>
  <div>
    <div class="d-flex"
         :class="{'justify-content-end': isNewPayment, 'justify-content-between': !isNewPayment}">
      <b-btn v-if="!isNewPayment" @click="openHistoryModal">{{$t('buttons.common.history')}}</b-btn>
      <history-modal-content></history-modal-content>
      <div class="button-group">
        <b-btn v-if="isActivePayment && currentRoutePermissions.change_partnerpayment"
               variant="outline-primary"
               @click="refundPayment" size="sm">
          {{$t('buttons.payments.refund')}}
        </b-btn>
        <b-btn v-if="isActivePayment && currentRoutePermissions.change_partnerpayment" variant="success"
               @click="changePaymentStatus('move_status_forward')" size="sm">
          {{ $t('buttons.common.complete')}}
        </b-btn>
        <b-btn v-if="!isActivePayment && !isNewPayment && currentRoutePermissions.change_partnerpayment"
               variant="success" @click="changePaymentStatus('move_status_back')"
               size="sm">
          {{ $t('buttons.common.to_active')}}
        </b-btn>
        <b-btn v-if="currentRoutePermissions.change_partnerpayment" variant="outline-success" @click="save"
               size="sm">{{ $t('buttons.common.save')}}
        </b-btn>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{paymentLabel}}
        <span v-if="!isNewPayment"
              :style="{color: isActivePayment ? 'green' : 'red'}"> - {{paymentData.status}}</span>
      </h3>
      <div class="icons-wrapper">
        {{paymentInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <div>
      <b-collapse id="collapse" v-model="showCollapse">
        <div class="filter-form">
          <b-row>
            <b-col>
              <label>{{$t('labels.payments.payment_number')}}</label>
              <input class="form-control" v-model="paymentData.payment_number" tabindex="1"
                     :class="{ required: !paymentData.payment_number && isActivePayment}"
                     :disabled="!isActivePayment || hasBoundedInvoices">
            </b-col>
            <b-col>
              <label>{{$t('labels.payments.payment_date')}}</label>
              <datepicker v-model="paymentData.date" lang="en" :not-after="new Date()" :tabindex="5"
                          placeholder=""
                          :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)" :first-day-of-week="1"
                          :class="{ required: !paymentData.date}"
              ></datepicker>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.real_recipient')}}</label>
              <multiselect v-model="paymentData.real_recipient" :max-height="200" :searchable="true"
                           placeholder="" :tabindex="9"
                           label="name" track-by="name" :options="partners"
                           :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label class="label">{{$t('labels.payments.payment_type')}}</label>
              <multiselect v-model="paymentData.type" :max-height="200" :searchable="true"
                           placeholder="" :options="money_flow"
                           :class="{ required: !paymentData.type}" :tabindex="2"
                           :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.currency')}}</label>
              <multiselect v-model="paymentData.currency" :max-height="200" :searchable="true"
                           placeholder="" :tabindex="6"
                           label="code" track-by="code" :options="currencies"
                           :disabled="hasBoundedInvoices || paymentData.additional || (!hasBoundedInvoices && isCompletedPayment)"
                           :class="{ required: !paymentData.currency}">
              </multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.real_payer')}}</label>
              <multiselect v-model="paymentData.real_payer" :max-height="200" :searchable="true"
                           placeholder="" :tabindex="10"
                           label="name" track-by="name" :options="partners"
                           :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.recipient')}}</label>
              <multiselect v-model="paymentData.recipient" :max-height="200" :searchable="true"
                           placeholder="" :tabindex="3"
                           label="name" track-by="name" :options="partners"
                           :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)"
                           :class="{ required: !paymentData.recipient}">
              </multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.payments.payment_sum')}}</label>
              <float-input v-model="paymentData.total" placeholder=""
                           :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)" :tabindex="7"
                           :class="{ required: !paymentData.total}"/>
              <div class="rate-information">{{paymentData.rate_data}}</div>
            </b-col>
            <b-col class="checkbox-wrapper">
              <label class="label">{{$t('labels.payments.payment_method')}}</label>
              <toggle-button @change="changePaymentMethod"
                             :labels="{checked: 'cash', unchecked: 'Non cash'}"
                             :width="75" :tabindex="11"
                             :disabled="!isNewPayment && (!isActivePayment || hasBoundedInvoices)"
                             :value="paymentData.cash"
                             :sync="true"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.payer')}}</label>
              <multiselect v-model="paymentData.payer" :max-height="200" :searchable="true"
                           placeholder="" :tabindex="4"
                           label="name" track-by="name" :options="partners"
                           :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)"
                           :class="{ required: !paymentData.payer}"></multiselect>
            </b-col>
            <b-col cols="4">
              <label>{{$t('labels.payments.payment_sum')}}({{base_currency.code}})</label>
              <float-input v-model="paymentData.total_base_curr" :tabindex="8" :disabled="true"/>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.notes')}}</label>
              <b-form-textarea v-model="paymentData.notes" placeholder="" :tabindex="12">
              </b-form-textarea>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="checkbox-wrapper" cols="4">
              <label class="label">{{$t('labels.payments.payment_form')}}</label>
              <toggle-button @change="changePaymentForm"
                             :labels="{checked: 'Additional', unchecked: 'Official'}"
                             :width="90" :tabindex="11"
                             :value="paymentData.additional"
                             :sync="true"
                             :disabled="hasBoundedInvoices || (!hasBoundedInvoices && isCompletedPayment)"
              />
            </b-col>
            <b-col cols="4">
              <label>{{$t('labels.payments.lc_costs')}} ({{ base_currency.code }})</label>
              <float-input class="form-control" v-model.number="paymentData.lc_cost" :disabled="!isActivePayment"/>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
    </div>
    <div v-if="!isNewPayment">
      <b-card no-body>
        <b-tabs card>
          <b-tab active>
            <template slot="title">
              <portal-target name="invoices_tab"></portal-target>
            </template>
            <portal to="invoices_tab">{{$t('tabs.payments.invoices')}}</portal>
            <b-row class="d-flex justify-content-between">
              <b-col>
                <b-btn v-if="isActivePayment && currentRoutePermissions.change_partnerpayment"
                       variant="secondary"
                       size="sm" @click="deleteInvoices">
                  {{$t('buttons.payments.delete_invoices')}}
                </b-btn>
              </b-col>
              <b-col>
                <div class="d-flex justify-content-end button-group margin-0">
                  <b-btn variant="outline-primary" size="sm" v-if="currentRoutePermissions.change_partnerpayment"
                         :disabled="!isActivePayment || isIncomingPayment" @click="payPurchaseInvoices">
                    {{$t('buttons.purchase_invoices.pay_purchase_invoices')}}
                  </b-btn>
                  <b-btn variant="primary" size="sm"
                         v-if="currentRoutePermissions.change_partnerpayment"
                         @click="automaticallyPayInvoices"
                         :disabled="isPayInvoiceBtnDisabled"
                         class="tool-tip" data-toggle="tooltip" data-placement="top"
                         title="You can pay invoices with the same currency as payment currency">
                    {{$t('buttons.payments.pay_invoices')}}
                  </b-btn>
                  <b-btn v-if="currentRoutePermissions.change_partnerpayment" variant="secondary"
                         :disabled="!isActivePayment" size="sm" @click="openAddInvoiceModal">
                    {{$t('buttons.payments.add_invoices')}}
                  </b-btn>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue :style="gridHeight" class="ag-theme-balham"
                             :gridOptions="boundedInvoicesGrid"
                             :columnDefs="boundedInvoicesColumns" :rowData="boundedInvoicesData"
                             :pinnedBottomRowData="boundedInvoicesPinnedRowData"
                             @rowDoubleClicked="openPayInvoiceModal"
                             @selectionChanged="selectionChangeBoundedInvoices"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
                <div class="balance-wrapper d-flex row ml-0 justify-content-between">
                  <div>
                    <div class="label text-bold">
                      {{$t('modal_dialogs.payments.payment_balance')}}:
                    </div>
                    <div>
                      <div class="text-bold" :class="{'red-text': isNegativeBalance}">
                        {{paymentData.balance_str}}
                      </div>
                      <div :class="{'red-text': isNegativeBalance}">
                        {{paymentData.balance_base_curr_str}}
                      </div>
                    </div>
                  </div>
                  <div v-if="!isActivePayment">
                    <div  class="label text-bold red-text">
                      {{$t('labels.payments.refund')}}:
                    </div>
                    <div>
                      <div class="text-bold" :class="{'red-text': isNegativeBalance}">
                        {{paymentData.refund_str}}
                      </div>
                      <div :class="{'red-text': isNegativeBalance}">
                        {{paymentData.refund_base_curr_str}}
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-modal ref="addInvoiceModal" @hidden="resetInvoiceModal" :no-close-on-backdrop="true"
                     :no-enforce-focus="true" size="lg">
              <div slot="modal-title">
                {{$t('modal_dialogs.payments.add_invoice')}}
              </div>
              <div class="container">
                <b-row class="justify-content-between">
                  <b-col cols="2">
                    <label>{{$t('labels.payments.money_flow')}}</label>
                  </b-col>
                  <b-col cols="5">
                    <multiselect v-model="invoiceData.money_flow" :max-height="200"
                                 :taggable="false" @input="loadInvoices()"
                                 placeholder="" :options="money_flow"
                                 :multiple="false"></multiselect>
                  </b-col>
                  <b-col offset="2" class=" d-flex align-items-center">
                    <input type="checkbox" id="showPaidInvoices"
                           v-model="invoiceData.paid_status" @change="loadInvoices()">
                    <label for="showPaidInvoices" class="margin-left-5">Show paid invoices</label>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <input class="form-control" placeholder="" @input="setQuickFilter" ref="invoiceFilter">
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <ag-grid-vue :style="gridHeight" class="ag-theme-balham"
                                 :gridOptions="invoiceListGrid" :columnDefs="invoiceListColumns"
                                 :rowData="invoiceListData" @rowSelected="setSelectedInvoices"
                                 @selectionChanged="selectionsChanged" :postProcessPopup="postProcessPopup">
                    </ag-grid-vue>
                    <b-row>
                      <b-col class="d-flex justify-content-between">
                        <div class="label">{{$t('labels.common.selected')}}:</div>
                        <div>
                          <div v-for="(item,key) in this.listCurrencySymbolsOfInvoiceList" :key="key" v-if="item">
                            {{item.toLocaleString('ru-RU', {maximumFractionDigits: 2})}} {{key}}
                          </div>
                        </div>
                      </b-col>
                      <b-col class="d-flex justify-content-between">
                        <div class="label">{{$t('labels.common.balance')}}:</div>
                        <div>
                          <div>
                            {{invoiceData.approximate_payment_balance}} {{paymentData.currency_symbol}}
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </div>
              <div slot="modal-footer" class="w-100 text-center">
                <b-btn size="sm" variant="success" @click="addInvoices('close')">
                  {{$t('buttons.common.add_and_exit')}}
                </b-btn>
                <b-btn size="sm" variant="success" @click="addInvoices">
                  {{$t('buttons.common.add')}}
                </b-btn>
              </div>
            </b-modal>
            <b-modal ref="paymentForInvoiceModal" :no-close-on-backdrop="true" :no-enforce-focus="true">
              <div slot="modal-title">{{$t('modal_dialogs.payments.payment_for')}}
                <span class="underline">{{dataForPayInvoice.invoice_number}}</span>
              </div>
              <div class="container">
                <b-row>
                  <b-col cols="4">
                    <span>{{$t('modal_dialogs.payments.invoice_balance')}}:</span>
                  </b-col>
                  <b-col cols="5">
                    {{dataForPayInvoice.invoice_balance_str}}
                    ({{dataForPayInvoice.invoice_balance_in_payment_currency_str}})
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="3">
                    {{$t('modal_dialogs.payments.sum_of_payment')}}
                    <input class="form-control align-self-bottom" type="number" @input="convertSum"
                           v-model="dataForPayInvoice.value_in_payment_currency">
                  </b-col>
                  <b-col cols="1" class="symbol padding-0 text-center d-flex">
                    <span class="align-self-bottom">{{dataForPayInvoice.payment_currency_symbol}}
                      <span v-if="!invoiceCurrencyEqualPaymentCurrency">=</span>
                    </span>
                  </b-col>
                  <b-col cols="3" v-if="showBaseCurrSumField">
                    {{$t('labels.common.sum')}} ({{ base_currency.code }})
                    <input class="form-control align-self-bottom" v-model="dataForPayInvoice.value_in_base_curr" disabled>
                  </b-col>
                  <b-col cols="1" class="symbol padding-0 text-center d-flex" v-if="showBaseCurrSumField">
                    <span class="align-self-bottom">€ =</span>
                  </b-col>
                  <b-col cols="3" v-if="!invoiceCurrencyEqualPaymentCurrency">
                    {{$t('labels.common.sum')}} (inv. cur.)
                    <input class="form-control align-self-bottom" v-model="dataForPayInvoice.value_in_invoice_currency"
                           disabled>
                  </b-col>
                  <b-col cols="1" class="symbol padding-0 text-center d-flex"
                         v-if="!invoiceCurrencyEqualPaymentCurrency">
                    <span class="align-self-bottom">{{dataForPayInvoice.invoice_currency_symbol}}</span>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="3">
                    <label>{{$t('labels.payments.payment_date')}}</label>
                    <datepicker v-model="dataForPayInvoice.payment_date_for_transfer" lang="en"
                                :width="170"
                                placeholder=""
                                @change="changeDateForRate"
                                :not-after="new Date()"
                                :clearable="false"
                                :first-day-of-week="1"
                    ></datepicker>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <label>{{$t('common_content.rate')}} {{ base_currency.code }}/{{dataForPayInvoice.payment_currency_code}}
                      {{$t('common_content.on')}} {{dataForPayInvoice.payment_date_for_transfer}}</label>
                    <span v-if="currentLang !== 'ru'">is</span>
                    <span class="text-bold text-cursive">
                      {{dataForPayInvoice.payment_currency_rate &&
                      dataForPayInvoice.payment_currency_rate.toLocaleString('ru-RU', {minimumFractionDigits: 5})}}
                    </span>
                  </b-col>
                </b-row>
                <b-row v-if="!invoiceCurrencyEqualPaymentCurrency">
                  <b-col>
                    <label>{{$t('common_content.rate')}} {{ base_currency.code }}/{{dataForPayInvoice.invoice_currency_code}}
                      {{$t('common_content.on')}} {{dataForPayInvoice.payment_date_for_transfer}}</label>
                    <span v-if="currentLang !== 'ru'">is</span>
                    <span class="text-bold text-cursive">
                      {{dataForPayInvoice.invoice_currency_rate &&
                      dataForPayInvoice.invoice_currency_rate.toLocaleString('ru-RU', {minimumFractionDigits: 5})}}
                    </span>
                  </b-col>
                </b-row>
              </div>
              <div slot="modal-footer" class="w-100 text-center">
                <b-btn size="sm" variant="success" @click="payInvoice">{{$t('buttons.common.confirm')}}</b-btn>
              </div>
            </b-modal>
          </b-tab>
          <b-tab>
            <template slot="title">
              <portal-target name="documents_tab"></portal-target>
            </template>
            <portal to="documents_tab">{{$t('tabs.common.documents')}}</portal>
            <app-documents :documents="documents" :isCollapse="showCollapse" :url="commonUrl + 'attachments/'"
                           @loadDocuments="loadDocuments"></app-documents>
          </b-tab>
          <b-tab>
            <template slot="title">
              <portal-target name="comments_tab"></portal-target>
            </template>
            <portal to="comments_tab">{{$t('tabs.common.comments')}}</portal>
            <app-comments :comments="comments" :url="commonUrl + 'comments/'"
                          @loadComments="loadComments"></app-comments>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import moment from 'moment';
  import {
    handlerResponse,
    setNotifyData,
    getArrayByProperty,
  } from '@/helpers/commonHelpers';
  import FloatInput from '@/components/common/FloatInput';
  import Comments from '@/components/common/Comments';
  import Documents from '@/components/common/Documents';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { Decimal } from 'decimal.js';
  import { gridMixins } from '@/mixins'
  import { DATE_FORMAT } from '@/constants';
  import { INVOICE } from "@/constants/invoiceTypes";

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      Datepicker,
      'ag-grid-vue': AgGridVue,
      FloatInput,
      'app-comments': Comments,
      'app-documents': Documents,
      HistoryModalContent
    },
    data() {
      return {
        showCollapse: true,
        selectedBoundedInvoices: [],
        selectedInvoicesRows: [],
        paymentData: {
          rate_data: '',
          payment_number: '',
          total: null,
          total_base_currency: '',
          date: '',
          balance: '',
          notes: '',
          currency: null,
          payer: null,
          recipient: null,
          real_recipient: null,
          real_payer: null,
          created_by: '',
          created: '',
          status: '',
          type: '',
          currency_symbol: '',
          additional: false,
          lc_cost: 0
        },
        invoiceData: {
          money_flow: '',
          paid_status: false,
          approximate_payment_balance: ''
        },
        dataForPayInvoice: {},
        dataForChargeOffInvoice: {},
      }
    },
    methods: {
      prepareSavedData() {
        return {
          payment_number: this.paymentData.payment_number,
          payer: this.paymentData.payer.id,
          recipient: this.paymentData.recipient ? this.paymentData.recipient.id : null,
          currency: this.paymentData.currency ? this.paymentData.currency.id : null,
          total: this.paymentData.total,
          total_base_curr: this.paymentData.total_base_curr,
          lc_cost: this.paymentData.lc_cost,
          date: this.paymentData.date ? moment(this.paymentData.date).format(DATE_FORMAT) : '',
          notes: this.paymentData.notes,
          cash: this.paymentData.cash,
          mode: this.paymentData.additional ? 'additional' : 'official',
          type: this.paymentData.type,
          real_recipient: this.paymentData.real_recipient ? this.paymentData.real_recipient.id : null,
          real_payer: this.paymentData.real_payer ? this.paymentData.real_payer.id : null
        }
      },
      save() {
        const savedData = this.prepareSavedData();
        if (this.isNewPayment) {
          this.$http.post(`partner/payments/`, savedData)
            .then(handlerResponse).then((response) => {
            this.paymentData = {...response};
            // this.invoiceData.money_flow = response.type;
            this.paymentData.additional = response.mode === 'additional';
            this.$notify(setNotifyData('Success', 'Payment is saved!', 'success'));
            this.$router.push({path: `/payments/${response.id}`});
            this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        } else {
          this.$http.put(`partner/payments/${this.param}/`, savedData).then(handlerResponse).then(() => {
            this.$notify(setNotifyData('Success', 'Payment is saved!', 'success'));
            this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
            this.getPaymentInfo();
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        }
      },
      changePaymentMethod() {
        this.$set(this.paymentData, 'cash', !this.paymentData.cash)
      },
      changePaymentForm() {
        this.$set(this.paymentData, 'additional', !this.paymentData.additional)

        let currency = this.currencies.find(item => item.code === this.base_currency.code)
        if (!this.paymentData.additional && this.isNewPayment) {
          currency = null
        }
        this.$set(this.paymentData, 'currency', currency)
      },
      async getPaymentInfo(type) {
        this.$http.get(`partner/payments/${this.param}/`).then(handlerResponse).then((response) => {
          this.paymentData = {...response};
          this.$store.dispatch('location/setPathName', response.payment_number);
          this.paymentData.additional = response.mode === 'additional';
          // if (type === 'initialSettings') this.invoiceData.money_flow = response.type;
        });
      },
      async changePaymentStatus(status) {
        const isMainInvoicePaid = !!this.boundedInvoicesData.find(invoice => invoice.invoice_type === INVOICE);
        const isLcCostNotEmptyOrZero = this.paymentData.lc_cost > 0;
        if (!isMainInvoicePaid && isLcCostNotEmptyOrZero && status === 'move_status_forward') {
          const result = await this.$swal.fire({
            icon: 'warning',
            text: this.$t('alerts.lc_will_not_distributed'),
            showCancelButton: true,
            confirmButtonColor: '#1bc14d',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$i18n.t(`common_content.yes`),
            cancelButtonText: this.$i18n.t(`buttons.common.cancel`)
          })
          if (result.value) {
            try {
              await this.$http.post(`partner/payments/${this.param}/${status}/`).then(handlerResponse)
              this.$notify(setNotifyData('Success', 'Payment is saved!', 'success'));
              await this.getPaymentInfo();
              await this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
            } catch (e) {
              notificationsRenderer(e.body, 'error')
            }
          }
        } else {
          try {
            await this.$http.post(`partner/payments/${this.param}/${status}/`).then(handlerResponse)
            await this.getPaymentInfo();
            await this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
          } catch (e) {
            notificationsRenderer(e.body, 'error')
          }
        }
      },
      async refundPayment() {
        const refundData = {
          balance: 0,
          refund: this.paymentData.balance
        };
        const savedData = this.prepareSavedData();
        const payloadData = {
          ...refundData,
          ...savedData
        }
        try {
          await this.$http.post(`partner/payments/${this.param}/move_status_forward/`).then(handlerResponse);
          await this.$http.put(`partner/payments/${this.param}/`, payloadData).then(handlerResponse);
          await this.$notify(setNotifyData('Success', 'Payment has been refunded!', 'success'));
          await this.getPaymentInfo();
          await this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
        } catch (error) {
          console.log(error);
          notificationsRenderer(error.body, 'error');
        }
      },
      openAddInvoiceModal() {
        this.loadInvoices();
        this.selectionsChanged();
        this.$refs['addInvoiceModal'].show();
      },
      addInvoices(event) {
        const invoices = this.selectedInvoicesRows.map(e => ({id: e.id, invoice_class_name: e.invoice_class_name}));
        if (invoices.length) {
          this.$http.post(`partner/payments/${this.param}/invoice_transfers/`, {invoices}).then(handlerResponse)
            .then(() => {
              this.selectedInvoicesRows = [];
              this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
              this.getPaymentInfo();
              this.$notify(setNotifyData('Success', 'Invoices has been successfully bounded !', 'success'));
              if (event === 'close') {
                this.$refs['addInvoiceModal'].hide();
              } else {
                this.loadInvoices();
              }
            }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        }
      },
      resetInvoiceModal() {
        this.selectedInvoicesRows = [];
        this.invoiceData.paid_status = false;
      },
      setSelectedInvoices(event) {
        if (event.type === 'rowSelected' && event.node.selected) {
          if (!this.selectedInvoicesRows.find(item => item.id === event.data.id)) {
            this.selectedInvoicesRows.push(event.data);
          }
        } else {
          this.selectedInvoicesRows = this.selectedInvoicesRows.filter(item => item.id !== event.data.id);
        }
      },
      retrieveSelectedInvoices() {
        this.$nextTick(() => {
          this.invoiceListGrid.api.forEachNode((node) => {
            if (this.selectedInvoicesRows.find(invoice => invoice.id === node.data.id)) {
              node.setSelected(true);
            }
          });
        })
      },
      async loadInvoices() {
        const loader = this.$loading.show();
        this.$store.commit('payment/setInvoiceList', []);
        const params = {
          invoice_money_flow: this.invoiceData.money_flow,
          is_paid: this.invoiceData.paid_status,
          mode: this.paymentData.additional ? 'additional' : 'official'
        };
        await this.$store.dispatch('payment/loadInvoiceListData', {id: this.param, params});
        loader.hide();
        this.retrieveSelectedInvoices();
      },
      deleteInvoices() {
        const transfers = this.selectedBoundedInvoices.map(e => {
          return {id: e.id, invoice_class_name: e.invoice_class_name}
        });
        if (transfers.length) {
          this.$http.delete(`partner/payments/${this.param}/invoice_transfers/`, {body: {transfers}}).then(() => {
            this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
            this.getPaymentInfo();
            this.$notify(setNotifyData('Success', 'Invoices has been successfully unbounded !', 'success'));
          });
        }
      },
      setQuickFilter({target: {value}}) {
        this.invoiceListGrid.api.setQuickFilter(value);
      },
      selectionsChanged() {
        for (const key in this.listCurrencySymbolsOfInvoiceList) {
          if (this.listCurrencySymbolsOfInvoiceList.hasOwnProperty(key)) {
            this.listCurrencySymbolsOfInvoiceList[key] = this.selectedInvoicesRows.filter(item =>
              item['currency_symbol'] === key).reduce((prev, next) => {
              if (this.paymentData.type === 'incoming') {
                if (next.money_flow === 'incoming') {
                  return Decimal.add(prev, next.balance).toFixed(2);
                } else if (next.money_flow === 'outgoing' && (next.type !== 'CREDIT' && next.type !== 'MARGIN CORRECT')) {
                  return Decimal.sub(prev, next.balance).toFixed(2);
                } else if (next.money_flow === 'outgoing' && (next.type === 'CREDIT' || next.type === 'MARGIN CORRECT')) {
                  return Decimal.add(prev, next.balance).toFixed(2);
                }
              } else if (this.paymentData.type === 'outgoing') {
                if (next.money_flow === 'outgoing' && (next.type !== 'CREDIT' && next.type !== 'MARGIN CORRECT')) {
                  return Decimal.add(prev, next.balance).toFixed(2);
                } else if (next.money_flow === 'outgoing' && (next.type === 'CREDIT' || next.type === 'MARGIN CORRECT')) {
                  return Decimal.sub(prev, next.balance).toFixed(2);
                } else if (next.money_flow === 'incoming') {
                  return Decimal.sub(prev, next.balance).toFixed(2);
                }
              } else {
                return prev + 0
              }
            }, 0);
          }
        }
        const selectedInvoicesBalance = this.selectedInvoicesRows.reduce((prev, next) => {
          if (this.paymentData.currency.code === next.currency_code && this.paymentData.type === 'incoming') {
            if (next.money_flow === 'incoming') {
              return Decimal.add(prev, next.balance).toFixed(2);
            } else if (next.money_flow === 'outgoing' && (next.type === 'CREDIT' || next.type === 'MARGIN CORRECT')) {
              return Decimal.add(prev, next.balance).toFixed(2);
            } else if (next.money_flow === 'outgoing' && (next.type !== 'CREDIT' || next.type !== 'MARGIN CORRECT')) {
              return Decimal.sub(prev, next.balance).toFixed(2);
            }
          } else if (this.paymentData.currency.code === next.currency_code && this.paymentData.type === 'outgoing') {
            if (next.money_flow === 'incoming') {
              return Decimal.sub(prev, next.balance).toFixed(2);
            } else if (next.money_flow === 'outgoing' && (next.type === 'CREDIT' || next.type === 'MARGIN CORRECT')) {
              return Decimal.sub(prev, next.balance).toFixed(2);
            } else if (next.money_flow === 'outgoing' && (next.type !== 'CREDIT' || next.type !== 'MARGIN CORRECT')) {
              return Decimal.add(prev, next.balance).toFixed(2);
            }
          } else {
            return prev + 0
          }
        }, 0);

        this.invoiceData.approximate_payment_balance = Decimal.sub(this.paymentData.balance, selectedInvoicesBalance).toFixed(2);
      },
      openPayInvoiceModal(row) {
        if (this.isActivePayment) {
          this.$http.get(`partner/payments/${this.param}/invoice_transfers/${row.data.invoice_class_name}/${row.data.id}/`)
            .then(handlerResponse).then((response) => {
            this.dataForPayInvoice = {...response};
            this.convertSum();
            this.$refs['paymentForInvoiceModal'].show();
          });
        }
      },
      async payInvoice() {
        const payedData = {
          payment_date_for_transfer: moment(this.dataForPayInvoice.payment_date_for_transfer).format(DATE_FORMAT),
          value_in_invoice_currency: this.dataForPayInvoice.value_in_invoice_currency,
          value_in_payment_currency: this.dataForPayInvoice.value_in_payment_currency
        };
        try {
          await this.$http.put(
              `partner/payments/${this.param}/invoice_transfers/${this.dataForPayInvoice.invoice_class_name}/${this.dataForPayInvoice.id}/`,
              payedData
          ).then(handlerResponse)
          await this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
          await this.getPaymentInfo();
          this.$refs['paymentForInvoiceModal'].hide();
        } catch (e) {
          notificationsRenderer(e.body, 'error')
        }
      },
      convertSum() {
        this.dataForPayInvoice.value_in_base_curr = (this.dataForPayInvoice.value_in_payment_currency / this.dataForPayInvoice.payment_currency_rate).toFixed(2);
        if (!this.invoiceCurrencyEqualPaymentCurrency) {
          this.dataForPayInvoice.value_in_invoice_currency = (this.dataForPayInvoice.value_in_base_curr * this.dataForPayInvoice.invoice_currency_rate).toFixed(2);
          if (this.dataForPayInvoice.payment_currency_code === this.base_currency.code) {
            const convertedBalance = (this.dataForPayInvoice.invoice_balance / this.dataForPayInvoice.invoice_currency_rate).toFixed(2);
            this.dataForPayInvoice.invoice_balance_in_payment_currency_str = `${convertedBalance} ${this.dataForPayInvoice.payment_currency_symbol}`;
          } else {
            const convertedBalance = (this.dataForPayInvoice.invoice_balance * this.dataForPayInvoice.payment_currency_rate / this.dataForPayInvoice.invoice_currency_rate).toFixed(2);
            this.dataForPayInvoice.invoice_balance_in_payment_currency_str = `${convertedBalance} ${this.dataForPayInvoice.payment_currency_symbol}`;
          }
        } else {
          this.dataForPayInvoice.value_in_invoice_currency = this.dataForPayInvoice.value_in_payment_currency;
        }
      },
      changeDateForRate() {
        const loader = this.$loading.show();
        this.dataForPayInvoice.payment_date_for_transfer = moment(this.dataForPayInvoice.payment_date_for_transfer).format(DATE_FORMAT);
        const params = {
          date: this.dataForPayInvoice.payment_date_for_transfer
        };
        this.$http.get(`woodware/list/base_currency_rates/`, {params}).then(handlerResponse).then((response) => {
          this.dataForPayInvoice.invoice_currency_rate = response[`${this.base_currency.code}/${this.dataForPayInvoice.invoice_currency_code}`];
          this.dataForPayInvoice.payment_currency_rate = response[`${this.base_currency.code}/${this.dataForPayInvoice.payment_currency_code}`];
          this.convertSum();
          loader.hide();
        });
      },
      payPurchaseInvoices() {
        this.$http.post(`partner/payments/${this.param}/autopay_purchase_invoices/`).then(handlerResponse).then(() => {
          this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
          this.getPaymentInfo();
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.body, 'error');
        })
      },
      automaticallyPayInvoices() {
        const transfers = getArrayByProperty(this.selectedBoundedInvoices, 'id');
        this.$http.post(`partner/payments/${this.param}/pay_invoices/`, {transfers}).then(() => {
          this.$notify(setNotifyData('', 'Selected invoices have been payed successfully', 'success'));
          this.getPaymentInfo();
          this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
        })
      },
      loadComments() {
        this.$store.dispatch('payment/loadCommentsList', {id: this.param});
      },
      loadDocuments() {
        this.$store.dispatch('payment/loadDocumentsList', {id: this.param});
      },
      selectionChangeBoundedInvoices() {
        this.selectedBoundedInvoices = this.boundedInvoicesGrid.api.getSelectedRows();
      },
      openHistoryModal() {
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      clearData() {
        this.$store.commit('payment/setBoundedInvoices', []);
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapState('woodware', ['partners', 'base_currency']),
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapState('payment', ['boundedInvoicesPinnedRowData', 'boundedInvoicesData', 'boundedInvoicesColumns',
        'invoiceListColumns', 'invoiceListPinnedRowData', 'listCurrencySymbolsOfInvoiceList', 'comments',
        'invoiceListData', 'documents']),
      ...mapState('payments', ['money_flow']),
      ...mapGetters('payment', {
        invoiceListGrid: 'invoiceListGrid',
        boundedInvoicesGrid: 'boundedInvoicesGrid'
      }),
      param() {
        return this.$route.params.id;
      },
      isNewPayment() {
        return this.param === 'new';
      },
      isActivePayment() {
        return this.paymentData.status === 'active';
      },
      isCompletedPayment() {
        return this.paymentData.status === 'completed';
      },
      isIncomingPayment() {
        return this.paymentData.type === 'incoming';
      },
      paymentLabel() {
        return this.isNewPayment ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.payment')}` :
          `${this.$i18n.t('common_content.payment')} / ${this.paymentData.payment_number}`
      },
      paymentInfo() {
        return this.isNewPayment ? '' : `${this.paymentData.created} / ${this.$i18n.t('common_content.creator')}: ${this.paymentData.created_by}`
      },
      commonUrl() {
        return `partner/payments/${this.param}/`;
      },
      isNegativeBalance() {
        return this.paymentData.balance < 0;
      },
      showBaseCurrSumField() {
        if (this.dataForPayInvoice.invoice_currency_code === this.base_currency.code || this.dataForPayInvoice.payment_currency_code === this.base_currency.code) {
          return false;
        } else if (this.invoiceCurrencyEqualPaymentCurrency) {
          return false;
        }
        return true;
      },
      invoiceCurrencyEqualPaymentCurrency() {
        return this.dataForPayInvoice.invoice_currency_code === this.dataForPayInvoice.payment_currency_code;
      },
      currentLang() {
        return this.$i18n.locale;
      },
      hasBoundedInvoices() {
        return !!(this.boundedInvoicesData && this.boundedInvoicesData.length);
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 360px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 360px)'};
        }
      },
      isPayInvoiceBtnDisabled() {
        return this.selectedBoundedInvoices.length === 0 ||
          (this.paymentData.currency && !getArrayByProperty(this.selectedBoundedInvoices, 'invoice_currency_code')
            .every(item => item === this.paymentData.currency.code)) || !this.isActivePayment;
      },
      historySettings() {
        return {url: `partner_payment/?id=${this.param}`, title: this.paymentLabel.split('/')[1]}
      }
    },
    beforeMount() {
      this.$store.dispatch('woodware/getPartners', {is_active: true});
      this.$store.dispatch('generic/getCurrencies');
      if (!this.isNewPayment) {
        this.getPaymentInfo('initialSettings');
        this.loadComments();
        this.$store.dispatch('payment/loadBoundedInvoices', {id: this.param});
        this.$store.dispatch('payment/loadDocumentsList', {id: this.param});
      }
    }
  }
</script>

<style scoped>
  .rate-information {
    font-size: 12px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/accounts/payments/Payment.vue